import Button from '@core/components/v2/Button/Button'
import { Tooltip } from '@core/components/v2/Tooltip'

import { FORMAT_TOOLBAR_GROUPS, getFormatTooltip, type FormatCommand } from './config'
import {
    BoldIcon,
    BulletedListIcon,
    FormatIcon,
    ItalicIcon,
    LinkIcon,
    MentionIcon,
    NumberedListIcon,
    StrikethroughIcon,
    UnderlineIcon,
} from './icons'
import { cx } from './utils'

interface FooterProps {
    disabled?: boolean
    isEmpty: boolean
    isSubmitting?: boolean
    formatOpen: boolean
    activeFormats: Record<string, boolean>
    pinned: boolean
    showPinned: boolean
    pinnedLabel: string
    showFormat: boolean
    showMention: boolean
    showSubmit: boolean
    submitLabel: string
    onToggleFormat: () => void
    onFormatCommand: (command: string) => void
    onMentionClick?: () => void
    onPinnedChange: (pinned: boolean) => void
    onSubmit: () => void
}

const FORMAT_ICONS: Record<FormatCommand, () => JSX.Element> = {
    bold: () => <BoldIcon />,
    italic: () => <ItalicIcon />,
    underline: () => <UnderlineIcon />,
    strikethrough: () => <StrikethroughIcon />,
    bulletedList: () => <BulletedListIcon />,
    numberedList: () => <NumberedListIcon />,
    link: () => <LinkIcon />,
}

export function Footer({
    disabled = false,
    isEmpty,
    isSubmitting = false,
    formatOpen,
    activeFormats,
    pinned,
    showPinned,
    pinnedLabel,
    showFormat,
    showMention,
    showSubmit,
    submitLabel,
    onToggleFormat,
    onFormatCommand,
    onMentionClick,
    onPinnedChange,
    onSubmit,
}: FooterProps) {
    return (
        <div className="CKEditor__footer">
            <div className="CKEditor__footerRow">
                <div className="CKEditor__footerLeft">
                    {showFormat && (
                        <div className="CKEditor__formatWrap">
                            <button
                                type="button"
                                className={cx(
                                    'CKEditor__iconBtn',
                                    formatOpen && 'CKEditor__iconBtn--active'
                                )}
                                aria-label="Formatting"
                                aria-pressed={formatOpen}
                                aria-expanded={formatOpen}
                                disabled={disabled}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={onToggleFormat}
                            >
                                <FormatIcon />
                            </button>

                            <div
                                className={cx(
                                    'CKEditor__formatPopover',
                                    formatOpen && 'CKEditor__formatPopover--open'
                                )}
                                role="toolbar"
                                aria-label="Text formatting"
                                aria-hidden={!formatOpen}
                            >
                                {FORMAT_TOOLBAR_GROUPS.map((group, groupIndex) => (
                                    <div key={groupIndex} className="CKEditor__formatGroup">
                                        {groupIndex > 0 ? <span className="CKEditor__formatDivider" /> : null}
                                        {group.map((item) => (
                                            <Tooltip
                                                key={item.command}
                                                content={getFormatTooltip(item)}
                                                placement="top"
                                                disabled={!formatOpen}
                                            >
                                                <button
                                                    type="button"
                                                    className={cx(
                                                        'CKEditor__iconBtn',
                                                        'CKEditor__iconBtn--format',
                                                        activeFormats[item.command] && 'CKEditor__iconBtn--active'
                                                    )}
                                                    aria-label={getFormatTooltip(item)}
                                                    aria-pressed={Boolean(activeFormats[item.command])}
                                                    tabIndex={formatOpen ? 0 : -1}
                                                    disabled={disabled}
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => onFormatCommand(item.command)}
                                                >
                                                    {FORMAT_ICONS[item.command]()}
                                                </button>
                                            </Tooltip>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {showMention && (
                        <button
                            type="button"
                            className="CKEditor__iconBtn"
                            aria-label="Mention"
                            disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={onMentionClick}
                        >
                            <MentionIcon />
                        </button>
                    )}
                </div>

                <div className="CKEditor__footerRight">
                    {showPinned && (
                        <label className="CKEditor__pinned">
                            <input
                                type="checkbox"
                                checked={pinned}
                                disabled={disabled}
                                onChange={(event) => onPinnedChange(event.target.checked)}
                                onMouseDown={(event) => event.preventDefault()}
                            />
                            <span>{pinnedLabel}</span>
                        </label>
                    )}

                    {showSubmit && (
                        <Button
                            variant="primary"
                            size="small"
                            disabled={disabled || isEmpty}
                            isLoading={isSubmitting}
                            onClick={onSubmit}
                        >
                            {submitLabel}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}
